import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import {
    DAY_PACKAGES,
    PER_HOUR,
    PER_MINUTE,
} from './constants';
import { validateDatesAgainstTimeSlots } from './cartPrefillHelpers';
import {
    getMonthsForDateRange,
    getMonthsToFetch,
} from './dateRangeHelpers';
import { getStartOf } from './dates';

// RAS-2520: was a hand-written subset that silently omitted threeDaysPrice/
// fourDaysPrice/fiveDaysPrice. DAY_PACKAGES (constants.js) is the canonical list.
const DAY_BASED_PACKAGES = DAY_PACKAGES;
const TIME_BASED_PACKAGES = [PER_HOUR, PER_MINUTE];
const DAILY_PACKAGE_TYPE = 'daily';
const HOURLY_PACKAGE_TYPE = 'hourly';

/**
 * Determine package type from start and end dates
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {string} - 'daily' or 'hourly'
 */
export const determinePackageType = (startDate, endDate) => {
    const days = moment(endDate).diff(moment(startDate), 'days');
    if (days >= 1) return DAILY_PACKAGE_TYPE;
    return HOURLY_PACKAGE_TYPE;
};

/**
 * RAS-2520: the modality an EXISTING booking was actually made in.
 *
 * determinePackageType above infers modality from duration alone, which is all that was
 * available before listings could be booked in more than one category. Now that they can
 * (the booking panel's Daily/Hourly/Minute toggle), duration is ambiguous at the
 * boundaries - and a cart item already records the exact tier it was booked with on its
 * own `package` field, so prefer that and keep duration inference only for legacy
 * entries written before the field existed.
 *
 * @param {String} packageKey - the booking's stored price key, e.g. 'perHourPrice'
 * @param {Date|String} startDate
 * @param {Date|String} endDate
 * @returns {'daily'|'hourly'}
 */
export const getBookingPackageType = (packageKey, startDate, endDate) => {
    if (DAY_BASED_PACKAGES.includes(packageKey)) return DAILY_PACKAGE_TYPE;
    if (TIME_BASED_PACKAGES.includes(packageKey)) return HOURLY_PACKAGE_TYPE;
    return determinePackageType(startDate, endDate);
};

/**
 * Get listing timezone from Algolia item
 * @param {Object} item - Algolia search hit
 * @returns {string|null} - Timezone string or null
 */
export const getListingTimeZone = item => {
    return item?.availabilityPlan?.timezone || null;
};

/**
 * Get monthly time slots for a listing from Redux state
 * @param {Object} state - Redux state
 * @param {string} listingId - Listing ID
 * @returns {Object} - Monthly time slots structure { [monthId]: { timeSlots: [...] } }
 */
export const getMonthlyTimeSlotsForListing = (state, listingId) => {
    return state?.StoreProfilePage?.monthlyTimeSlotsByListing?.[listingId] || {};
};

/**
 * Check which months need fetching for a listing
 * @param {string} listingId - Listing ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} timeZone - Timezone
 * @param {Object} state - Redux state
 * @returns {Array<string>} - Array of month IDs to fetch
 */
export const getMonthsToFetchForListing = (listingId, startDate, endDate, timeZone, state) => {
    const neededMonths = getMonthsForDateRange(startDate, endDate, timeZone);
    const monthlyTimeSlots = getMonthlyTimeSlotsForListing(state, listingId);
    return getMonthsToFetch(neededMonths, monthlyTimeSlots);
};

/**
 * Fetch time slots for months needed for a date range
 * @param {Function} dispatch - Redux dispatch
 * @param {Function} getState - Redux getState
 * @param {Function} fetchTimeSlotsAction - Thunk action
 * @param {string} listingId - Listing ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} timeZone - Timezone
 * @returns {Promise} - Resolves when all months are fetched
 */
export const fetchTimeSlotsForListingDateRange = (
    dispatch,
    getState,
    fetchTimeSlotsAction,
    listingId,
    startDate,
    endDate,
    timeZone
) => {
    if (!startDate || !endDate || !listingId || !timeZone) {
        return Promise.resolve();
    }

    const state = getState();
    const monthsToFetch = getMonthsToFetchForListing(listingId, startDate, endDate, timeZone, state);

    if (monthsToFetch.length === 0) {
        return Promise.resolve();
    }

    const fetchPromises = monthsToFetch.map(monthId => {
        // Get the start and end of the month in the listing's timezone
        const monthStart = moment.tz(monthId, 'YYYY-MM', timeZone)
            .startOf('month').toDate();

        const monthEnd = moment.tz(monthId, 'YYYY-MM', timeZone)
            .startOf('month').add(1, 'month').toDate();


        // Always start from start of today (00:00) if month includes today
        // This ensures we fetch all time slots for today, not just from current time
        const now = moment().tz(timeZone);
        const todayStart = now.startOf('day').toDate();
        const rangeStart = moment(monthStart).isBefore(todayStart)
            ? todayStart
            : monthStart;

        // Skip if the whole month is in the past
        if (moment(monthEnd).isSameOrBefore(now)) {
            console.log('monthEnd is in the past', monthEnd);
            return null;
        }

        return dispatch(fetchTimeSlotsAction(listingId, rangeStart, monthEnd, timeZone));
    });

    return Promise.all(fetchPromises);
};

/**
 * Check availability for a listing and date range
 * @param {string} listingId - Listing ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} timeZone - Timezone
 * @param {string} packageType - Package type ('daily' or 'hourly')
 * @param {Object} state - Redux state
 * @returns {boolean|null} - true if available, false if not, null if cannot determine
 */
export const checkListingAvailability = (
    listingId,
    startDate,
    endDate,
    timeZone,
    packageType,
    state
) => {
    if (!startDate || !endDate || !timeZone || !packageType) {
        return false;
    }

    const monthlyTimeSlots = getMonthlyTimeSlotsForListing(state, listingId);

    if (!monthlyTimeSlots || Object.keys(monthlyTimeSlots).length === 0) {
        return null; // Cannot determine - still loading
    }

    return validateDatesAgainstTimeSlots(startDate, endDate, monthlyTimeSlots, timeZone, packageType);
};

